import { withErrorBoundary } from '@sentry/react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { useMutation } from 'urql';
import { assets } from '../../assets';
import Button from '../../components/buttons/button';
import { BlurredContainer } from '../../components/containers/containers';
import DefaultScreen from '../../components/containers/default-screen';
import StyledSelect, { SelectOption } from '../../components/inputs/styled-select';
import { Fallback } from '../../components/utils/error-boundary';
import { GraphqlOperations } from '../../logic/graphql/operations';
import Auth from '../../logic/redux/auth';
import { Paths } from '../../navigation/paths';
import { useIntl, FormattedMessage } from 'react-intl';

const LogoutSettings = (): JSX.Element => {
  const intl = useIntl();
  const [selectedTimeout, setSelectedTimeout] = useState<number | undefined>(undefined);

  const changeTimeout = useMutation(
    GraphqlOperations.mutations.update_user_timeout.mutationString,
  )[1];
  const { addToast } = useToasts();

  const timeout = useSelector(Auth.select.selectAuthToken)?.expiry;

  const handleTimeoutSelectionChange = (option: SelectOption): void => {
    setSelectedTimeout(option.value as number);
  };
  const handleChangeClick = (): void => {
    if (selectedTimeout) {
      const variables = GraphqlOperations.mutations.update_user_timeout.createVars(selectedTimeout);
      changeTimeout(variables)
        .then((result) => {
          if (result && !result.error) {
            setSelectedTimeout(undefined);
            addToast(
              intl.formatMessage({ id: 'profilePage.settings.logoutSettings.toast.success' }),
              {
                appearance: 'success',
                autoDismiss: true,
              },
            );
          } else {
            addToast(
              intl.formatMessage({ id: 'profilePage.settings.logoutSettings.toast.error' }),
              {
                appearance: 'error',
                autoDismiss: false,
              },
            );
          }
          return true;
        })
        .catch(() => {
          addToast(intl.formatMessage({ id: 'profilePage.settings.logoutSettings.toast.error' }), {
            appearance: 'error',
            autoDismiss: false,
          });
        });
    }
  };

  return (
    <DefaultScreen
      background={assets.images.profile_bg}
      title={intl.formatMessage({ id: 'profilePage.settings.logoutSettings.title' })}
      backUrl={Paths.settings}
    >
      <BlurredContainer>
        <p className="text-14 mb-6">
          <FormattedMessage id="profilePage.settings.logoutSettings.description" />
        </p>
        <StyledSelect
          className="mb-8"
          onChange={handleTimeoutSelectionChange}
          initialValue={timeout}
          options={[
            {
              value: 15,
              label: intl.formatMessage({
                id: 'profilePage.settings.logoutSettings.timeOptions.15',
              }),
            },
            {
              value: 30,
              label: intl.formatMessage({
                id: 'profilePage.settings.logoutSettings.timeOptions.30',
              }),
            },
            {
              value: 45,
              label: intl.formatMessage({
                id: 'profilePage.settings.logoutSettings.timeOptions.45',
              }),
            },
            {
              value: 60,
              label: intl.formatMessage({
                id: 'profilePage.settings.logoutSettings.timeOptions.60',
              }),
            },
          ]}
        />
        <Button inactive={!selectedTimeout} loading={false} onClick={handleChangeClick}>
          <FormattedMessage id="profilePage.settings.logoutSettings.changeButton" />
        </Button>
      </BlurredContainer>
    </DefaultScreen>
  );
};

export default withErrorBoundary(LogoutSettings, { fallback: Fallback });
